import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, UserCheck, UserPlus, TrendingUp, ArrowRight, Sparkles } from 'lucide-react';

interface FunnelStage {
  label: string;
  value: number;
  color: string;
  icon?: React.ReactNode;
  sublabel?: string;
}

interface AnimatedFunnelChartProps {
  title: string;
  data: FunnelStage[];
}

const AnimatedFunnelChart: React.FC<AnimatedFunnelChartProps> = ({ title, data }) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const maxValue = Math.max(...data.map(d => d.value));

  return (
    <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
      <div className="flex items-center justify-between mb-8">
        <h3 className="text-2xl font-bold text-gray-900">{title}</h3>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Sparkles className="w-4 h-4" />
          <span>Conversion Flow</span>
        </div>
      </div>

      <div className="space-y-6">
        {data.map((stage, index) => {
          const percentage = (stage.value / maxValue) * 100;
          const conversionRate = index > 0 
            ? ((stage.value / data[index - 1].value) * 100).toFixed(1)
            : '100.0';
          const isHovered = hoveredIndex === index;

          return (
            <div key={index} className="relative">
              {/* Funnel Stage */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.15 }}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                className="relative"
              >
                {/* Stage Container */}
                <div className="relative">
                  {/* Funnel Shape */}
                  <motion.div
                    className="relative overflow-hidden rounded-2xl"
                    style={{
                      background: `linear-gradient(135deg, ${stage.color}20 0%, ${stage.color}40 100%)`,
                      border: `2px solid ${stage.color}40`,
                    }}
                    animate={{
                      scale: isHovered ? 1.02 : 1,
                      boxShadow: isHovered 
                        ? `0 20px 40px ${stage.color}30`
                        : '0 4px 6px rgba(0,0,0,0.1)',
                    }}
                    transition={{ duration: 0.3 }}
                  >


                    {/* Content */}
                    <div className="relative p-6">
                      <div className="flex items-center justify-between mb-4">
                        {/* Left: Icon & Label */}
                        <div className="flex items-center gap-4">
                          <motion.div
                            className="w-14 h-14 rounded-xl flex items-center justify-center shadow-lg"
                            style={{ backgroundColor: stage.color }}
                            animate={{
                              rotate: isHovered ? 360 : 0,
                              scale: isHovered ? 1.1 : 1,
                            }}
                            transition={{ duration: 0.5 }}
                          >
                            {stage.icon || <Users className="w-7 h-7 text-white" />}
                          </motion.div>

                          <div>
                            <h4 className="text-xl font-bold text-gray-900">{stage.label}</h4>
                            {stage.sublabel && (
                              <p className="text-sm text-gray-500">{stage.sublabel}</p>
                            )}
                          </div>
                        </div>

                        {/* Right: Value & Conversion */}
                        <div className="text-right">
                          <motion.div
                            className="text-4xl font-black text-gray-900"
                            animate={{
                              scale: isHovered ? 1.1 : 1,
                            }}
                          >
                            {stage.value.toLocaleString()}
                          </motion.div>
                          {index > 0 && (
                            <div className="flex items-center gap-1 justify-end mt-1">
                              <TrendingUp className="w-4 h-4 text-green-500" />
                              <span className="text-sm font-semibold text-green-600">
                                {conversionRate}% conversion
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Progress Bar */}
                      <div className="relative h-3 bg-white/50 rounded-full overflow-hidden">
                        <motion.div
                          className="absolute inset-y-0 left-0 rounded-full"
                          style={{ backgroundColor: stage.color }}
                          initial={{ width: 0 }}
                          animate={{ width: `${percentage}%` }}
                          transition={{ duration: 1.5, delay: index * 0.15 + 0.3 }}
                        />
                        
                        {/* Shimmer Effect */}
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-40"
                          animate={{
                            x: ['-100%', '200%'],
                          }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                            repeatDelay: 1,
                          }}
                        />
                      </div>

                      {/* Percentage Label */}
                      <div className="mt-2 text-right">
                        <span className="text-sm font-semibold" style={{ color: stage.color }}>
                          {percentage.toFixed(1)}% of max
                        </span>
                      </div>
                    </div>
                  </motion.div>

                  {/* Funnel Width Visualization */}
                  <motion.div
                    className="absolute left-0 right-0 h-full top-0 pointer-events-none"
                    initial={{ clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)' }}
                    animate={{
                      clipPath: `polygon(
                        ${(100 - percentage) / 2}% 0%, 
                        ${100 - (100 - percentage) / 2}% 0%, 
                        ${100 - (100 - percentage) / 2}% 100%, 
                        ${(100 - percentage) / 2}% 100%
                      )`,
                    }}
                    transition={{ duration: 1.5, delay: index * 0.15 }}
                  />
                </div>
              </motion.div>

              {/* Arrow Connector */}
              {index < data.length - 1 && (
                <motion.div
                  className="flex justify-center my-2"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.15 + 0.5 }}
                >
                  <motion.div
                    animate={{
                      y: [0, 5, 0],
                    }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                    }}
                  >
                    <ArrowRight
                      className="w-6 h-6 text-gray-400 transform rotate-90"
                    />
                  </motion.div>
                </motion.div>
              )}

              {/* Hover Details */}
              <AnimatePresence>
                {isHovered && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="mt-4 p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200">
                      <div className="grid grid-cols-3 gap-4">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-gray-900">
                            {stage.value.toLocaleString()}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">Total Count</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-blue-600">
                            {percentage.toFixed(1)}%
                          </div>
                          <div className="text-xs text-gray-500 mt-1">Of Maximum</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-green-600">
                            {conversionRate}%
                          </div>
                          <div className="text-xs text-gray-500 mt-1">Conversion</div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>

      {/* Summary Statistics */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: data.length * 0.15 + 0.5 }}
        className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4"
      >
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 text-center border border-blue-200">
          <Users className="w-8 h-8 mx-auto text-blue-600 mb-2" />
          <div className="text-2xl font-bold text-gray-900">
            {data[0]?.value.toLocaleString() || 0}
          </div>
          <div className="text-sm text-gray-600">Initial Stage</div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 text-center border border-green-200">
          <UserCheck className="w-8 h-8 mx-auto text-green-600 mb-2" />
          <div className="text-2xl font-bold text-gray-900">
            {data[data.length - 1]?.value.toLocaleString() || 0}
          </div>
          <div className="text-sm text-gray-600">Final Stage</div>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 text-center border border-purple-200">
          <TrendingUp className="w-8 h-8 mx-auto text-purple-600 mb-2" />
          <div className="text-2xl font-bold text-gray-900">
            {data.length > 0
              ? ((data[data.length - 1].value / data[0].value) * 100).toFixed(1)
              : 0}%
          </div>
          <div className="text-sm text-gray-600">Overall Conversion</div>
        </div>
      </motion.div>
    </div>
  );
};

export default AnimatedFunnelChart;

